import React, {useEffect} from 'react';
import styles from './ErrorModal.module.css';
import {useLandingPagesContext} from '@dazn/lp-web/src/contexts/LandingPageContext';
import useDeviceResolution from '@dazn/lp-web/src/hooks/useDeviceResolution';
import classNames from 'classnames';
import {filterImageData} from './ErrorModalUtils';
import {triggerNotificationImpressionEvent} from '../../analytics/fe-events/triggerNotificationImpressionEvent';
import {triggerNotificationClickEvent} from '../../analytics/fe-events/triggerNotificationClickEvent';

const getUrl = (key: string, entry: {image?: any}) => {
    return entry?.image?.[key] ?? entry?.image?.['default'];
};

const CloseIcon = () => {
    return (
        <>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.71 7.62215L17.29 6.20215L12 11.5021L6.71001 6.20215L5.29001 7.62215L10.59 12.9121L5.29001 18.2021L6.71001 19.6221L12 14.3221L17.29 19.6221L18.71 18.2021L13.41 12.9121L18.71 7.62215Z"
                    fill="#F9FAFA"
                />
            </svg>
        </>
    );
};

export const ErrorModal = () => {
    const {setShowModal, modalData} = useLandingPagesContext();
    const {title, description, buttons, showClose, httpStatusCode} =
        modalData || {};

    const isCanalPlusLp = window.location.href.includes('canal-plus');
    const isCanalPlusUnsubscribedUser =
        isCanalPlusLp && httpStatusCode === '403';
    const isCanalPlusTravellingUser = isCanalPlusLp && httpStatusCode === '401';

    const isAmazonLp = window.location.href.includes('amazon');
    const isAmazonUnsubscribedUser = isAmazonLp && httpStatusCode === '404';
    const benefitMismatchSupported = isAmazonLp && httpStatusCode === '403'; //travelling
    const benefitMismatchUnsupported = isAmazonLp && httpStatusCode === '406';

    const imageData = filterImageData(modalData);

    const {device} = useDeviceResolution();

    useEffect(() => {
        if (isCanalPlusUnsubscribedUser) {
            triggerNotificationImpressionEvent({
                itemName: 'canal plus subscription required'
            });
        }

        if (isCanalPlusTravellingUser) {
            triggerNotificationImpressionEvent({
                itemName: 'canal plus travelling message'
            });
        }

        if (isAmazonUnsubscribedUser) {
            triggerNotificationImpressionEvent({
                itemName: 'lwa dazn sub requires'
            });
        }

        if (benefitMismatchUnsupported) {
            triggerNotificationImpressionEvent({
                itemName: 'lwa country not supported'
            });
        }

        if (benefitMismatchSupported) {
            triggerNotificationImpressionEvent({
                itemName: 'lwa travelling message'
            });
        }
    }, []);

    const image = getUrl(device, imageData);

    const navigateToHome = (button: any) => {
        const navigationLink = button?.fields?.navigationLink;
        const analyticsEventId = button?.fields?.eventId ?? '';

        if (isCanalPlusUnsubscribedUser) {
            triggerNotificationClickEvent({
                itemName: 'canal plus subscription required',
                clickedElement: analyticsEventId
            });
        }

        if (isCanalPlusTravellingUser) {
            triggerNotificationClickEvent({
                itemName: 'canal plus travelling message',
                clickedElement: analyticsEventId
            });
        }

        if (isAmazonUnsubscribedUser) {
            triggerNotificationClickEvent({
                itemName: 'lwa dazn sub requires',
                clickedElement: analyticsEventId
            });
        }

        if (benefitMismatchUnsupported) {
            triggerNotificationClickEvent({
                itemName: 'lwa country not supported',
                clickedElement: analyticsEventId
            });
        }

        if (benefitMismatchSupported) {
            triggerNotificationClickEvent({
                itemName: 'lwa travelling message',
                clickedElement: analyticsEventId
            });
        }

        if (navigationLink) {
            window.location.href = navigationLink;
        } else {
            setShowModal(false);
        }
    };

    const handleClose = () => {
        if (isCanalPlusUnsubscribedUser) {
            triggerNotificationClickEvent({
                itemName: 'canal plus subscription required',
                clickedElement: 'close_cta'
            });
        }

        if (isCanalPlusTravellingUser) {
            triggerNotificationClickEvent({
                itemName: 'canal plus travelling message',
                clickedElement: 'close_cta'
            });
        }

        if (isAmazonUnsubscribedUser) {
            triggerNotificationClickEvent({
                itemName: 'lwa dazn sub requires',
                clickedElement: 'x_icon'
            });
        }

        if (benefitMismatchUnsupported) {
            triggerNotificationClickEvent({
                itemName: 'lwa country not supported',
                clickedElement: 'x_icon'
            });
        }

        if (benefitMismatchSupported) {
            triggerNotificationClickEvent({
                itemName: 'lwa travelling message',
                clickedElement: 'x_icon'
            });
        }

        setShowModal(false);
    };

    if (!modalData) {
        return null;
    }

    return (
        <div className={styles.outer}>
            <div
                className={styles.inner}
                style={{
                    backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(8, 14, 18) 50.07%, #091116 78.94%, #0C161C 100%), url(${image}`
                }}
            >
                {showClose && (
                    <div className={styles.topDiv} onClick={handleClose}>
                        <CloseIcon />
                    </div>
                )}
                <div className={styles.content}>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.description}>{description}</div>
                    {buttons?.map((button: any, index: number) => {
                        const isPrimaryButton =
                            button?.fields?.type.toLowerCase() === 'primary';
                        return (
                            <button
                                key={index}
                                onClick={() => navigateToHome(button)}
                                className={classNames(styles.buttonStyles, {
                                    [styles.secondaryButtonStyles]:
                                        !isPrimaryButton
                                })}
                            >
                                {button?.fields?.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
